/**
 * The overview panel: what was built, described in the words the CRM holds, plus
 * the brief and the three dates that bound the work.
 *
 * The delivery fields are FREE TEXT and are printed exactly as stored. Nothing
 * here goes through `t()`, and that is a decision rather than an omission: the
 * schema says so in as many words (`platform String? // "wordpress" | "husly" |
 * ... free text, not an enum`, `pages String? // free text ("1-5", "10+")`), and
 * half of the values arrived from a fourteen-year-old Perfex install. A `t()`
 * over an unknown value prints the key itself, so translating them would show a
 * paying client `portal.platform.wordpress-multisite` where the operator wrote a
 * sentence.
 */
import type { ReactNode } from "react";
import { Badge } from "@wa/ui";
import { useT, useI18n } from "../../../../i18n/I18nProvider";
import type { PortalProjectDetail } from "../../../../lib/portal";
import { fmtDate } from "../../../../lib/format";

function Chips({ values }: { values: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((v) => (
        <Badge key={v} variant="secondary">{v}</Badge>
      ))}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

export function OverviewPanel({ detail }: { detail: PortalProjectDetail }) {
  const t = useT();
  const { locale } = useI18n();
  const o = detail.overview;

  // Each row is drawn only when it has an answer. A grid of six "-" is not a
  // delivery record, it is a screen telling the client we never filled one in -
  // and half these columns are empty for every project that predates them.
  const rows: { key: string; label: string; node: ReactNode }[] = [];
  if (o?.platform) rows.push({ key: "platform", label: t("portal.projectField.platform"), node: o.platform });
  if (o?.logoStatus) rows.push({ key: "logoStatus", label: t("portal.projectField.logoStatus"), node: o.logoStatus });
  if (o?.pages) rows.push({ key: "pages", label: t("portal.projectField.pages"), node: o.pages });
  if (o?.services.length) {
    rows.push({ key: "services", label: t("portal.projectField.services"), node: <Chips values={o.services} /> });
  }
  if (o?.features.length) {
    rows.push({ key: "features", label: t("portal.projectField.features"), node: <Chips values={o.features} /> });
  }

  const dates: { key: string; label: string; value: string }[] = [];
  if (detail.startDate) dates.push({ key: "started", label: t("portal.projectField.started"), value: fmtDate(detail.startDate, locale) });
  if (detail.deadline) dates.push({ key: "deadline", label: t("portal.projectField.deadline"), value: fmtDate(detail.deadline, locale) });
  if (detail.completedAt) dates.push({ key: "completed", label: t("portal.projectField.completed"), value: fmtDate(detail.completedAt, locale) });

  const empty = rows.length === 0 && dates.length === 0 && !detail.description;

  return (
    <div className="space-y-6">
      {/* The same stored text the CRM shows, and the same rule: it holds real
          newlines, so without `whitespace-pre-wrap` the client reads the whole
          brief as one run-on paragraph. */}
      {detail.description && (
        <p className="whitespace-pre-wrap text-sm text-muted-foreground">{detail.description}</p>
      )}

      {rows.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold">{t("portal.projects.overviewTitle")}</h4>
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {rows.map((r) => <Field key={r.key} label={r.label}>{r.node}</Field>)}
          </dl>
        </div>
      )}

      {dates.length > 0 && (
        <dl className="grid gap-x-8 gap-y-4 border-t border-border pt-4 sm:grid-cols-3">
          {dates.map((d) => <Field key={d.key} label={d.label}>{d.value}</Field>)}
        </dl>
      )}

      {empty && (
        <p className="py-6 text-center text-sm text-muted-foreground">{t("portal.projects.linkedEmpty")}</p>
      )}
    </div>
  );
}
